"use client";

import { useMemo, useState } from "react";
import type { Product } from "@/types/product";
import { createInquiry } from "@/lib/api/inquiries";

function isValidName(v: string) {
  const s = v.trim();
  return /^[A-Za-z][A-Za-z\s'\-]{1,79}$/.test(s);
}

function isValidEmail(v: string) {
  const s = v.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function InquiryModal({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: Product;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const nameOk = useMemo(() => isValidName(name), [name]);
  const emailOk = useMemo(() => isValidEmail(email), [email]);
  const messageOk = useMemo(() => message.trim().length > 0, [message]);
  const canSubmit = useMemo(
    () => nameOk && emailOk && messageOk && !busy,
    [nameOk, emailOk, messageOk, busy]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg border border-neutral-200 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Send an inquiry
            </div>
            <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
              About: <span className="font-medium">{product.name}</span>
            </div>
          </div>
          <button
            onClick={() => {
              setDone(null);
              setErr(null);
              onClose();
            }}
            className="rounded-md border border-neutral-200 px-3 py-1 text-sm text-neutral-900 hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-800"
          >
            Close
          </button>
        </div>

        {done ? (
          <div className="mt-4 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-900 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100">
            <div className="font-medium">Inquiry sent</div>
            <div className="mt-1 text-neutral-700 dark:text-neutral-300">
              {done}
            </div>
          </div>
        ) : (
          <>
            {err ? (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
                {err}
              </div>
            ) : null}

            <div className="mt-4 grid gap-3">
              <div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="h-10 w-full rounded-md border px-3 text-sm outline-none transition border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:ring-neutral-100/10"
                />
                {name.trim().length > 0 && !nameOk ? (
                  <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Name must be letters only (spaces, - and ' allowed).
                  </div>
                ) : null}
              </div>

              <div>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  type="email"
                  className="h-10 w-full rounded-md border px-3 text-sm outline-none transition border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:ring-neutral-100/10"
                />
                {email.trim().length > 0 && !emailOk ? (
                  <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Enter a valid email.
                  </div>
                ) : null}
              </div>

              <div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Message"
                  className="min-h-[110px] w-full rounded-md border px-3 py-2 text-sm outline-none transition border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:ring-neutral-100/10"
                />
                {message.trim().length === 0 ? (
                  <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                    Message is required.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="h-10 rounded-md border border-neutral-200 px-4 text-sm text-neutral-900 hover:bg-neutral-50 disabled:opacity-60 dark:border-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-800"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                disabled={!canSubmit}
                onClick={async () => {
                  setBusy(true);
                  setErr(null);
                  try {
                    const resp = await createInquiry({
                      productId: product.id,
                      productSlug: product.slug,
                      productName: product.name,
                      name: name.trim(),
                      email: email.trim(),
                      message: message.trim(),
                    });
                    setDone(`Reference: ${resp.id}`);
                    setName("");
                    setEmail("");
                    setMessage("");
                  } catch (e: any) {
                    setErr(e?.message ?? "Failed to send inquiry");
                  } finally {
                    setBusy(false);
                  }
                }}
                className="h-10 rounded-md px-4 text-sm font-medium transition disabled:opacity-50 bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
              >
                {busy ? "Sending..." : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}